import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { SettingsProvider } from './contexts/SettingsProvider';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';
import Login from './pages/Login';

// Lazy-loaded route components for code splitting
const InventoryList = lazy(() => import('./pages/inventory/InventoryList').then(m => ({ default: m.InventoryList })));
const TransferWorkflow = lazy(() => import('./pages/inventory/TransferWorkflow').then(m => ({ default: m.TransferWorkflow })));
const KegDashboard = lazy(() => import('./pages/kegs/KegDashboard').then(m => ({ default: m.KegDashboard })));
const KegTracking = lazy(() => import('./pages/kegs/KegTracking').then(m => ({ default: m.KegTracking })));
const OverdueAlerts = lazy(() => import('./pages/kegs/OverdueAlerts').then(m => ({ default: m.OverdueAlerts })));
const BrewingOrders = lazy(() => import('./pages/contract-brewing/BrewingOrders').then(m => ({ default: m.BrewingOrders })));
const DistributionDashboard = lazy(() => import('./pages/distribution/DistributionDashboard').then(m => ({ default: m.DistributionDashboard })));
const CustomerManagement = lazy(() => import('./pages/distribution/CustomerManagement').then(m => ({ default: m.CustomerManagement })));
const CustomerDetail = lazy(() => import('./pages/distribution/CustomerDetail').then(m => ({ default: m.CustomerDetail })));
const DriverTools = lazy(() => import('./pages/distribution/DriverTools').then(m => ({ default: m.DriverTools })));
const ProductsDashboard = lazy(() => import('./pages/products/ProductsDashboard').then(m => ({ default: m.ProductsDashboard })));
const PricingTiers = lazy(() => import('./pages/products/PricingTiers').then(m => ({ default: m.PricingTiers })));
const RNDDashboard = lazy(() => import('./pages/rnd/RNDDashboard').then(m => ({ default: m.RNDDashboard })));
const OTADashboard = lazy(() => import('./pages/ota/OTADashboard').then(m => ({ default: m.OTADashboard })));
const MerchandiseDashboard = lazy(() => import('./pages/merchandise/MerchandiseDashboard').then(m => ({ default: m.MerchandiseDashboard })));
const AnalyticsDashboard = lazy(() => import('./pages/analytics/AnalyticsDashboard').then(m => ({ default: m.AnalyticsDashboard })));
const HopDatabase = lazy(() => import('./pages/ingredients/HopDatabase').then(m => ({ default: m.HopDatabase })));
const IngredientBrowser = lazy(() => import('./pages/ingredients/IngredientBrowser').then(m => ({ default: m.IngredientBrowser })));
const RecipeList = lazy(() => import('./pages/recipes/RecipeList').then(m => ({ default: m.RecipeList })));
const RecipeEditor = lazy(() => import('./pages/recipes/RecipeEditor').then(m => ({ default: m.RecipeEditor })));
const WaterChemistry = lazy(() => import('./pages/water/WaterChemistry').then(m => ({ default: m.WaterChemistry })));
const CommunityHub = lazy(() => import('./pages/community/CommunityHub').then(m => ({ default: m.CommunityHub })));
const CollaborationHub = lazy(() => import('./pages/collaboration/CollaborationHub').then(m => ({ default: m.CollaborationHub })));
const TemperatureDashboard = lazy(() => import('./pages/monitoring/TemperatureDashboard').then(m => ({ default: m.TemperatureDashboard })));
const ColdChainDashboard = lazy(() => import('./pages/monitoring/ColdChainDashboard').then(m => ({ default: m.ColdChainDashboard })));
const WorkflowDashboard = lazy(() => import('./pages/workflows/WorkflowDashboard').then(m => ({ default: m.WorkflowDashboard })));
const AIDashboard = lazy(() => import('./pages/ai/AIDashboard').then(m => ({ default: m.AIDashboard })));
const QRTrackingDashboard = lazy(() => import('./pages/qr/QRTrackingDashboard').then(m => ({ default: m.QRTrackingDashboard })));
const BillingDashboard = lazy(() => import('./pages/billing/BillingDashboard').then(m => ({ default: m.BillingDashboard })));
const SustainabilityDashboard = lazy(() => import('./pages/sustainability/SustainabilityDashboard').then(m => ({ default: m.SustainabilityDashboard })));
const EventsDashboard = lazy(() => import('./pages/events/EventsDashboard').then(m => ({ default: m.EventsDashboard })));
const DeliveryZones = lazy(() => import('./pages/distribution/DeliveryZones').then(m => ({ default: m.DeliveryZones })));
const BeerClubDashboard = lazy(() => import('./pages/beer-club/BeerClubDashboard').then(m => ({ default: m.BeerClubDashboard })));
const LoyaltyDashboard = lazy(() => import('./pages/loyalty/LoyaltyDashboard').then(m => ({ default: m.LoyaltyDashboard })));
const MarketplaceDashboard = lazy(() => import('./pages/marketplace/MarketplaceDashboard').then(m => ({ default: m.MarketplaceDashboard })));
const SchedulingDashboard = lazy(() => import('./pages/scheduling/SchedulingDashboard').then(m => ({ default: m.SchedulingDashboard })));
const TipDashboard = lazy(() => import('./pages/tips/TipDashboard').then(m => ({ default: m.TipDashboard })));
const DTCShippingDashboard = lazy(() => import('./pages/dtc/DTCShippingDashboard').then(m => ({ default: m.DTCShippingDashboard })));
const BeerFinderDashboard = lazy(() => import('./pages/beer-finder/BeerFinderDashboard').then(m => ({ default: m.BeerFinderDashboard })));
const POSDashboard = lazy(() => import('./pages/pos/POSDashboard').then(m => ({ default: m.POSDashboard })));
const MenuBoardDashboard = lazy(() => import('./pages/menu-boards/MenuBoardDashboard').then(m => ({ default: m.MenuBoardDashboard })));
const MenuBoardDisplay = lazy(() => import('./pages/menu-boards/MenuBoardDisplay').then(m => ({ default: m.MenuBoardDisplay })));
const WebsiteBuilderDashboard = lazy(() => import('./pages/website-builder/WebsiteBuilderDashboard').then(m => ({ default: m.WebsiteBuilderDashboard })));
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })));
const CreateOrganization = lazy(() => import('./pages/onboarding/CreateOrganization').then(m => ({ default: m.CreateOrganization })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-3 text-sm text-gray-500">Loading...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="text-center py-20">
      <h1 className="text-6xl font-bold text-gray-200 mb-4">404</h1>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Page Not Found</h2>
      <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
      <a
        href="/"
        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        Back to Dashboard
      </a>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<Suspense fallback={<PageLoader />}><CreateOrganization /></Suspense>} />
      <Route path="/display/menu-board/:boardId" element={<Suspense fallback={<PageLoader />}><MenuBoardDisplay /></Suspense>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="inventory" element={<Suspense fallback={<PageLoader />}><InventoryList /></Suspense>} />
        <Route path="inventory/transfer" element={<Suspense fallback={<PageLoader />}><TransferWorkflow /></Suspense>} />
        <Route path="kegs" element={<Suspense fallback={<PageLoader />}><KegDashboard /></Suspense>} />
        <Route path="kegs/tracking" element={<Suspense fallback={<PageLoader />}><KegTracking /></Suspense>} />
        <Route path="kegs/overdue" element={<Suspense fallback={<PageLoader />}><OverdueAlerts /></Suspense>} />
        <Route path="contract-brewing" element={<Suspense fallback={<PageLoader />}><BrewingOrders /></Suspense>} />
        <Route path="distribution" element={<Suspense fallback={<PageLoader />}><DistributionDashboard /></Suspense>} />
        <Route path="distribution/customers" element={<Suspense fallback={<PageLoader />}><CustomerManagement /></Suspense>} />
        <Route path="distribution/customers/:id" element={<Suspense fallback={<PageLoader />}><CustomerDetail /></Suspense>} />
        <Route path="distribution/drivers" element={<Suspense fallback={<PageLoader />}><DriverTools /></Suspense>} />
        <Route path="distribution/zones" element={<Suspense fallback={<PageLoader />}><DeliveryZones /></Suspense>} />
        <Route path="products" element={<Suspense fallback={<PageLoader />}><ProductsDashboard /></Suspense>} />
        <Route path="products/pricing" element={<Suspense fallback={<PageLoader />}><PricingTiers /></Suspense>} />
        <Route path="rnd" element={<Suspense fallback={<PageLoader />}><RNDDashboard /></Suspense>} />
        <Route path="ota" element={<Suspense fallback={<PageLoader />}><OTADashboard /></Suspense>} />
        <Route path="merchandise" element={<Suspense fallback={<PageLoader />}><MerchandiseDashboard /></Suspense>} />
        <Route path="analytics" element={<Suspense fallback={<PageLoader />}><AnalyticsDashboard /></Suspense>} />
        <Route path="recipes" element={<Suspense fallback={<PageLoader />}><RecipeList /></Suspense>} />
        <Route path="recipes/:id" element={<Suspense fallback={<PageLoader />}><RecipeEditor /></Suspense>} />
        <Route path="water" element={<Suspense fallback={<PageLoader />}><WaterChemistry /></Suspense>} />
        <Route path="community" element={<Suspense fallback={<PageLoader />}><CommunityHub /></Suspense>} />
        <Route path="collaboration" element={<Suspense fallback={<PageLoader />}><CollaborationHub /></Suspense>} />
        <Route path="monitoring" element={<Suspense fallback={<PageLoader />}><TemperatureDashboard /></Suspense>} />
        <Route path="monitoring/cold-chain" element={<Suspense fallback={<PageLoader />}><ColdChainDashboard /></Suspense>} />
        <Route path="workflows" element={<Suspense fallback={<PageLoader />}><WorkflowDashboard /></Suspense>} />
        <Route path="ai" element={<Suspense fallback={<PageLoader />}><AIDashboard /></Suspense>} />
        <Route path="qr-tracking" element={<Suspense fallback={<PageLoader />}><QRTrackingDashboard /></Suspense>} />
        <Route path="billing" element={<Suspense fallback={<PageLoader />}><BillingDashboard /></Suspense>} />
        <Route path="hops" element={<Suspense fallback={<PageLoader />}><HopDatabase /></Suspense>} />
        <Route path="ingredients" element={<Suspense fallback={<PageLoader />}><IngredientBrowser /></Suspense>} />
        <Route path="sustainability" element={<Suspense fallback={<PageLoader />}><SustainabilityDashboard /></Suspense>} />
        <Route path="events" element={<Suspense fallback={<PageLoader />}><EventsDashboard /></Suspense>} />
        <Route path="beer-club" element={<Suspense fallback={<PageLoader />}><BeerClubDashboard /></Suspense>} />
        <Route path="loyalty" element={<Suspense fallback={<PageLoader />}><LoyaltyDashboard /></Suspense>} />
        <Route path="marketplace" element={<Suspense fallback={<PageLoader />}><MarketplaceDashboard /></Suspense>} />
        <Route path="scheduling" element={<Suspense fallback={<PageLoader />}><SchedulingDashboard /></Suspense>} />
        <Route path="tips" element={<Suspense fallback={<PageLoader />}><TipDashboard /></Suspense>} />
        <Route path="dtc-shipping" element={<Suspense fallback={<PageLoader />}><DTCShippingDashboard /></Suspense>} />
        <Route path="beer-finder" element={<Suspense fallback={<PageLoader />}><BeerFinderDashboard /></Suspense>} />
        <Route path="pos" element={<Suspense fallback={<PageLoader />}><POSDashboard /></Suspense>} />
        <Route path="menu-boards" element={<Suspense fallback={<PageLoader />}><MenuBoardDashboard /></Suspense>} />
        <Route path="website-builder" element={<Suspense fallback={<PageLoader />}><WebsiteBuilderDashboard /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<PageLoader />}><SettingsPage /></Suspense>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <OrganizationProvider>
              <SettingsProvider>
                <ToastProvider>
                  <ConfirmProvider>
                    <AppRoutes />
                  </ConfirmProvider>
                </ToastProvider>
              </SettingsProvider>
            </OrganizationProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
